<script setup lang="ts">
    interface LookbookCollageProps {
        data: LookbookCollageData;
    }
    const props = defineProps<LookbookCollageProps>();

    const { textColorClass, textColorStyle, buttonColorClass, buttonColorStyle, buttonLabelColorClass, buttonLabelColorStyle } = useStyleCustom(
        props.data.style,
    );

    const asset3Style = computed(() => {
        const type = props.data.style?.render ?? 'type1';
        return type === 'type2' ? 'lg:absolute lg:bottom-14 lg:right-0 lg:z-10 lg:max-w-xl' : null;
    });
</script>
<template>
    <section
        :id="data.codename"
        class="mol-lookbook-collage relative py-10 lg:my-10 lg:py-[10vh]">
        <atm-grid>
            <div class="col-span-2 md:col-span-12 lg:col-span-6">
                <div
                    v-if="data.asset1"
                    class="lb-collage-asset-1">
                    <lazy-atm-image
                        :data="data.asset1"
                        preset="3to2"
                        sizes="100vw md:1024px lg:1264px" />
                </div>

                <div class="mx-6 my-8 md:mx-8 lg:my-14 lg:flex lg:flex-row lg:justify-end">
                    <!-- container width / 2 - container ml (spoof a container width content alignment) -->
                    <div class="lg:w-[448px] xl:w-[568px]">
                        <div
                            :class="[textColorClass, 'relative flex flex-col gap-6']"
                            :id="data.anchor"
                            :style="textColorStyle">
                            <lazy-atm-heading
                                v-if="data.title"
                                level="h3">
                                <renderer-html :content="woomTextFormat(data.title)" />
                            </lazy-atm-heading>

                            <div
                                v-if="data.copy"
                                class="flex flex-col gap-4">
                                <renderer-html :content="woomTextFormat(data.copy)" />
                            </div>

                            <div>
                                <lazy-atm-cta-cms
                                    v-if="data.link?.label"
                                    :link="data.link"
                                    styling="outline-auto"
                                    :class="['mt-2', buttonColorClass, buttonLabelColorClass]"
                                    :style="[buttonColorStyle, buttonLabelColorStyle]">
                                    <renderer-html :content="woomTextFormat(data.link.label)" />
                                </lazy-atm-cta-cms>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-span-2 flex flex-col items-end md:col-span-12 lg:col-span-6">
                <div
                    v-if="data.asset2"
                    class="lb-collage-asset-2 w-full">
                    <lazy-atm-image
                        :data="data.asset2"
                        class="w-full"
                        preset="2to3"
                        sizes="100vw md:1024px lg:1264px" />
                </div>
                <div
                    v-if="data.asset3"
                    :class="['lb-collage-asset-3 w-full', asset3Style]">
                    <lazy-atm-image
                        :data="data.asset3"
                        class="w-full"
                        sizes="100vw md:1024px" />
                </div>
            </div>
        </atm-grid>
    </section>
</template>

<style scoped>
    @reference "#main.css";
    @variant lg {
        .lb-collage-asset-1,
        .lb-collage-asset-2,
        .lb-collage-asset-3 {
            transform: translateY(var(--translate));
        }
    }
</style>
